import { Event, GomarkePixelConfig, PixelResponse } from '@/types';
import { getTrackingCookies } from './getTrackingCookies';
import { getDeviceInfo } from './getDeviceInfo';
import { getFingerprint, getFingerprintSync } from './getFingerprint';
import { fetchWithTimeout } from './fetchWithTimeout';

export interface SendEventParams {
  visitorId: string;
  eventData: Event['data'];
  category: Event['category'];
  type: Event['type'];
  config: GomarkePixelConfig;
  sessionId: string;
  version: string;
  scriptUrl?: string;
}

declare global {
  interface Window {
    __gomarkePixelUnloading?: boolean;
  }
}

/**
 * Monta o Event a partir de params e fingerprint.
 * eventId opcional: quando passado (ex.: item.id da queue), garante idempotência no retry.
 * occurredAt opcional: timestamp do acontecimento (evita usar hora do flush como timestamp).
 */
export function buildEvent(
  params: SendEventParams,
  fingerprint: string,
  eventId?: string,
  occurredAt?: number
): Event {
  const {
    visitorId,
    category,
    type,
    eventData,
    config,
    sessionId,
    version,
    scriptUrl,
  } = params;
  const { _fbp, _fbc, _ga, _gcl_au } = getTrackingCookies();
  const deviceInfo = getDeviceInfo();
  const timestamp =
    occurredAt != null ? String(occurredAt) : Date.now().toString();
  return {
    category,
    type,
    visitor_id: visitorId,
    data: eventData,
    session_id: sessionId,
    timestamp,
    id: eventId ?? crypto.randomUUID(),
    pixel_code: config.pixel,
    fingerprint,
    identifiers: {
      fbp: _fbp ?? '',
      fbc: _fbc ?? '',
      ga: _ga ?? '',
      gcl_au: _gcl_au ?? '',
    },
    page: {
      url: typeof window !== 'undefined' ? window.location.href : '',
      title: typeof document !== 'undefined' ? document.title : '',
      referrer: typeof document !== 'undefined' ? document.referrer : '',
      path: typeof window !== 'undefined' ? window.location.pathname : '',
      hostname: typeof window !== 'undefined' ? window.location.hostname : '',
    },
    device: deviceInfo,
    meta: {
      version,
      pixel_source: 'gomarke',
      environment: 'production',
      script_url: scriptUrl ?? '',
      projectId: config.projectId ?? '',
      pageId: config.pageId ?? '',
    },
  };
}

function buildPayload(
  params: SendEventParams,
  fingerprint: string,
  eventId?: string,
  occurredAt?: number
): Event {
  return buildEvent(params, fingerprint, eventId, occurredAt);
}

/**
 * Envia um evento apenas via sendBeacon (síncrono). Usado no unload para
 * garantir envio sem fetch (fetch seria cancelado no F5/close).
 */
export function sendEventBeaconOnly(
  params: SendEventParams,
  eventId?: string,
  occurredAt?: number
): void {
  if (typeof window === 'undefined' || !navigator.sendBeacon) return;
  const fingerprint = getFingerprintSync();
  const payload = buildPayload(params, fingerprint, eventId, occurredAt);
  const endPointUrl = `${params.config.apiEndpoint}/pixels/public/event`;
  navigator.sendBeacon(endPointUrl, JSON.stringify(payload));
}

/**
 * Single Transport Rule:
 * - Normal (página visível): fetch com retry possível (queue re-enfileira).
 * - Unload/hidden: apenas sendBeacon; nunca fetch (evita cancelados no F5).
 */
export const sendEvent = async (
  params: SendEventParams
): Promise<PixelResponse> => {
  const { config } = params;
  const isUnload =
    (typeof document !== 'undefined' &&
      document.visibilityState === 'hidden') ||
    (typeof window !== 'undefined' && !!window.__gomarkePixelUnloading);

  const endPointUrl = `${config.apiEndpoint}/pixels/public/event`;

  if (isUnload) {
    const fingerprint = getFingerprintSync();
    const payload = buildPayload(params, fingerprint, undefined);
    if (config.debug) console.log('Sending event (beacon, unload):', payload);
    if (navigator.sendBeacon) {
      const ok = navigator.sendBeacon(endPointUrl, JSON.stringify(payload));
      return {
        success: ok,
        event_id: ok ? `beacon_${Date.now()}` : undefined,
        timestamp: Date.now(),
      };
    }
    return {
      success: false,
      error: 'sendBeacon unavailable',
      timestamp: Date.now(),
    };
  }

  const fingerprint = await getFingerprint();
  const payload = buildPayload(params, fingerprint, undefined);

  if (config.debug) console.log('Sending event (fetch):', payload);

  try {
    const timeoutMs = config.timeout ?? 5_000;
    const response = await fetchWithTimeout(endPointUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
      timeoutMs,
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    return {
      success: true,
      event_id: result.event_id || `fetch_${Date.now()}`,
      timestamp: Date.now(),
    };
  } catch (error) {
    if (config.debug) console.error('Error sending event:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Error sending event',
      timestamp: Date.now(),
    };
  }
};
